if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.refreshElements();
        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.bindThumbnailClicks();
        this.bindViewerArrows();
        this.updateViewerArrows();
        const initialMedia =
          this.elements.viewer?.querySelector('[data-media-id].is-active') ||
          this.elements.viewer?.querySelector('[data-media-id]');
        if (initialMedia) this.showMediaLoadingState(initialMedia);
        if (this.elements.thumbnails && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      refreshElements() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      bindThumbnailClicks() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || button.dataset.galleryBound === 'true') return;
          button.dataset.galleryBound = 'true';
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      bindViewerArrows() {
        this.elements.viewerPrev = this.querySelector('[data-gallery-viewer-prev]');
        this.elements.viewerNext = this.querySelector('[data-gallery-viewer-next]');
        if (this.elements.viewerPrev && this.elements.viewerPrev.dataset.galleryBound !== 'true') {
          this.elements.viewerPrev.dataset.galleryBound = 'true';
          this.elements.viewerPrev.addEventListener('click', () => this.stepViewerMedia(-1));
        }
        if (this.elements.viewerNext && this.elements.viewerNext.dataset.galleryBound !== 'true') {
          this.elements.viewerNext.dataset.galleryBound = 'true';
          this.elements.viewerNext.addEventListener('click', () => this.stepViewerMedia(1));
        }
      }

      getViewerMediaItems() {
        return Array.from(this.elements.viewer?.querySelectorAll('.product__media-item[data-media-id]') || []);
      }

      stepViewerMedia(direction) {
        const items = this.getViewerMediaItems();
        const currentIndex = items.findIndex((item) => item.classList.contains('is-active'));
        const nextIndex = (currentIndex < 0 ? 0 : currentIndex) + direction;
        if (nextIndex < 0 || nextIndex >= items.length) return;
        this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
      }

      updateViewerArrows() {
        const items = this.getViewerMediaItems();
        const currentIndex = items.findIndex((item) => item.classList.contains('is-active'));
        const index = currentIndex < 0 ? 0 : currentIndex;
        if (this.elements.viewerPrev) this.elements.viewerPrev.disabled = index <= 0;
        if (this.elements.viewerNext) this.elements.viewerNext.disabled = index >= items.length - 1;

        const currentPage = this.elements.viewer?.querySelector('.slider-counter--current');
        const totalPages = this.elements.viewer?.querySelector('.slider-counter--total');
        if (currentPage) currentPage.textContent = String(index + 1);
        if (totalPages) totalPages.textContent = String(items.length);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.showMediaLoadingState(event.detail.currentElement);
        this.updateViewerArrows();
      }

      setActiveMedia(mediaId, prepend, scroll = true) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!scroll) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.showMediaLoadingState(activeMedia);

        this.updateViewerArrows();

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      showMediaLoadingState(activeItem) {
        const spinner = activeItem.querySelector('.loading__spinner');
        if (!spinner) return;

        const image = activeItem.querySelector('.product__media img, .deferred-media__poster img');
        const hideSpinner = () => spinner.classList.add('hidden');

        if (!image || (image.complete && image.naturalWidth > 0)) {
          hideSpinner();
          return;
        }

        spinner.classList.remove('hidden');
        image.addEventListener('load', hideSpinner, { once: true });
        image.addEventListener('error', hideSpinner, { once: true });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
